import React, { useEffect, useMemo, useState } from "react";
import { withRouter } from "react-router-dom";
import HeaderContent from "../../components/layout/components/HeaderContent";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import SanitizedGrid from "../../components/SanitizedGrid";
import { Grid, makeStyles, Typography } from "@material-ui/core";
import {
  BooleanInput,
  Button,
  FormDataConsumer,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  useDataProvider,
  useNotify,
} from "react-admin";
import { ExportTypeEnum, exportTypeList } from "./const";
import { useSelector } from "react-redux";
import { ReduxState } from "ra-core";
import Alert from "@material-ui/lab/Alert";
import { useField, useFormState } from "react-final-form";
import capitalize from "lodash/capitalize";
import { Parser } from "json2csv";
import fileDownload from "js-file-download";
import ExportStudentInput from "../students/components/ExportStudentInput";
import ExportTypeInformationComponent from "./components/ExportTypeInformationComponent";
import {
  exportStudentValidation,
  generateExportStudentFilename,
} from "../students/tools";
import CustomDateRangeInput from "../../components/CustomDateRangeInput";
import WaveInput from "../assignments/components/WaveInput";
import CustomDatePickerInput from "../../components/CustomDatePickerInput";
import { AssignmentReportTypeEnum } from "../assignments/const";
import { exportTypeFormatter } from "./tools";
import ExportCommercialDocumentInput from "../commercialDocuments/components/ExportCommercialDocumentInput";
import {
  exportCommercialDocumentValidation,
  generateExportCommercialDocumentFilename,
} from "../commercialDocuments/tools";
import { StudentExportTypeEnum } from "../students/const";
import { notRenewedStudentStatusList } from "../dueRenewalStudents/const";
import { notRenewedCcaStudentStatusList } from "../ccaDueRenewalStudents/const";

interface IExportButton {
  setIsLoading?: (isLoading: boolean) => void;
}

const useStyles = makeStyles({
  form: {
    "& > .MuiCardContent-root": {
      padding: 0,
    },
  },
  exportTypeInformationContainer: {
    paddingLeft: "35px !important",
  },
  exportStudentInputContainer: {
    marginTop: "-35px",
    marginBottom: "35px",
  },
  exportInputContainer: {
    marginTop: "-15px",
    marginBottom: "35px",
  },
  filterByWaveInput: {
    marginBottom: "-25px",
  },
  filterByAllWavesInput: {
    marginTop: "-15px",
    marginBottom: "-25px",
  },
});

const ExportButton = ({ setIsLoading }: IExportButton) => {
  const { values } = useFormState();
  const { getRecordForCSV, getOne } = useDataProvider();
  const notify = useNotify();

  const [isCSVLoading, setCSVLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [progress, setProgress] = useState<{
    current: number | null;
    total: number | null;
  }>({
    current: 0,
    total: 0,
  });

  const commercialDocumentTypeLabel =
    values.commercialDocumentType === "Invoice" ? "Invoice" : "Credit Note";

  const isStudentMasterList =
    values.exportType === StudentExportTypeEnum.StudentMasterList;
  const studentMasterListValues = {
    isIncludeParent: isStudentMasterList ? true : values.isIncludeParent,
    isIncludeStudentSchedule: isStudentMasterList
      ? true
      : values.isIncludeStudentSchedule,
    isIncludeEstimatedRevenue: isStudentMasterList
      ? "yes"
      : values.isIncludeEstimatedRevenue,
    isIncludeActiveRoutePlan: isStudentMasterList
      ? true
      : values.isIncludeActiveRoutePlan,
  };

  const exportCSV = async () => {
    if (isCSVLoading) return;

    const filter: any = {
      SchoolId: values.school.id,
    };

    if (
      (values.exportType === StudentExportTypeEnum.Student ||
        values.exportType === StudentExportTypeEnum.StudentMasterList) &&
      values.isFilterByRegistrationDate &&
      values.startDate
    ) {
      filter.RegisterPeriodStartDate = values.startDate;
    }

    if (
      (values.exportType === StudentExportTypeEnum.Student ||
        values.exportType === StudentExportTypeEnum.StudentMasterList) &&
      values.isFilterByRegistrationDate &&
      values.endDate
    ) {
      filter.RegisterPeriodEndDate = values.endDate;
    }

    if (
      (values.exportType === StudentExportTypeEnum.Student ||
        values.exportType === StudentExportTypeEnum.StudentMasterList) &&
      values.isFilterByStatus &&
      values.status
    ) {
      filter.Status = values.status;
    }

    if (
      values.exportType === AssignmentReportTypeEnum.AreaReport ||
      values.exportType === AssignmentReportTypeEnum.PassengerReport
    ) {
      filter.EstimatedStartedAt = values.estimatedStartedAt;
    }

    // Apply Passenger Report specific filters
    if (values.exportType === AssignmentReportTypeEnum.PassengerReport) {
      if (values.isFilterByWave) {
        filter.BusSchedulePlanId = values.busSchedulePlanId;
      }

      filter.MultipleStatus = ["Ended", "ForceEnded", "EndedByAdmin"];
    }

    // Apply AM Bus Arrival Report specific filters
    if (values.exportType === AssignmentReportTypeEnum.AmBusArrivalReport) {
      if (
        values.busSchedulePlanIds &&
        values.busSchedulePlanIds.length > 0 &&
        !values.isDownloadAllAmWaves
      ) {
        filter.BusSchedulePlanIds = values.busSchedulePlanIds;
      }

      if (values.startDate) {
        filter.EstimatedStartedAt = values.startDate;
      }

      if (values.endDate) {
        filter.EstimatedEndedAt = values.endDate;
      }

      filter.RouteType = "ToSchool";
    }

    if (values.exportType === ExportTypeEnum.CommercialDocument) {
      filter.Type = values.commercialDocumentType;
      filter.StatusIn = ["Draft", "Unposted", "Posted"];
      filter.IssuedFromDate = values.startDate;
      filter.IssuedToDate = values.endDate;

      if (values.isFilterBySchool && values.schoolId) {
        try {
          const { data: school } = await getOne("schools", {
            id: values.schoolId,
          });

          if (!school || !school.mbcCode) {
            notify("Selected school is missing MBC Code", "warning");
            return;
          }

          filter.SchoolCode = school.mbcCode;
        } catch (e) {
          notify("Failed to fetch school info", "warning");
          return;
        }
      }

      if (values.isFilterByStatus) {
        filter.StatusIn = values.status;
      }

      if (
        values.isFilterByStatus &&
        values.commercialDocumentType === "CreditNote" &&
        values.status.includes("Posted")
      ) {
        filter.IsTaggedCreditNote = values.isTaggedCreditNote;
      }

      if (values.isFilterByDescription) {
        filter.Description = values.description;
      }
    }

    if (values.exportType === ExportTypeEnum.StudentRenewal) {
      if (values.academicYearId) {
        filter.AcademicYearId = values.academicYearId;
      }

      if (values.status) {
        filter.Status = values.status;
      }
    }

    if (values.exportType === ExportTypeEnum.CcaStudentRenewal) {
      if (values.ccaAcademicYearId) {
        filter.CcaAcademicYearId = values.ccaAcademicYearId;
      }

      if (values.status) {
        filter.Status = values.status;
      }
    }

    // Set resource based on export type
    let resource = values.exportType;
    switch (values.exportType) {
      case AssignmentReportTypeEnum.AreaReport:
      case AssignmentReportTypeEnum.AmBusArrivalReport:
        resource = "assignments";
        break;
      case AssignmentReportTypeEnum.PassengerReport:
        resource = "assignments/passengerReport";
        break;
      case AssignmentReportTypeEnum.AssignmentSummaryReport:
        resource = "busSchedulePlans";
        break;
      case StudentExportTypeEnum.StudentMasterList:
        resource = "students";
        break;
    }

    // Validate resource
    if (!resource || resource.trim() === "") {
      notify("Invalid export type.", "warning");
      return;
    }

    getRecordForCSV(resource, {
      filter,
      body: {
        ...values,
        ...studentMasterListValues,
        reportType: values.exportType,
        commercialDocumentType: values.commercialDocumentType,
      },
      callback: ({ isLoading, loadedRecordCount, totalRecords }: any) => {
        // Only update loading state once at start and end
        if (isLoading !== undefined) {
          setCSVLoading(isLoading);
          setIsLoading && setIsLoading(isLoading);
        }

        setProgress({
          current: loadedRecordCount,
          total: totalRecords,
        });
      },
    })
      .then(({ data }: any) => {
        const parser = new Parser();

        if (!data || data.length === 0) {
          let emptyMessage = `No ${exportTypeFormatter(values.exportType)} found for ${values.school.name}`;

          switch (values.exportType) {
            case AssignmentReportTypeEnum.PassengerReport:
              emptyMessage = `No ended assignment found for ${values.school.name}`;
              break;
            case ExportTypeEnum.CommercialDocument:
              emptyMessage = `No ${commercialDocumentTypeLabel.toLowerCase()} found`;
              break;
          }

          notify(emptyMessage, "warning");
          return;
        }

        const csvData = data.flat();

        let fileName = `Records - ${values.school.name}.csv`;
        switch (values.exportType) {
          case ExportTypeEnum.Parent:
            fileName = `Parent Records - ${values.school.name}.csv`;
            break;
          case StudentExportTypeEnum.Student:
            fileName = generateExportStudentFilename(
              values as formExportStudentValues,
            );
            break;
          case AssignmentReportTypeEnum.AreaReport:
            fileName = `Area Report ${values.school.name} ${values.estimatedStartedAt}.csv`;
            break;
          case AssignmentReportTypeEnum.PassengerReport:
            fileName = `Passenger Report ${values.school.name} ${values.estimatedStartedAt}.csv`;
            break;
          case AssignmentReportTypeEnum.AmBusArrivalReport:
            fileName = `AM Bus Arrival Report ${values.school.name} from ${values.startDate} to ${values.endDate}.csv`;
            break;
          case AssignmentReportTypeEnum.AssignmentSummaryReport:
            fileName = `Assignment Report ${values.school.name} (${values.startDate} - ${values.endDate}).csv`;
            break;
          case ExportTypeEnum.CommercialDocument:
            fileName = generateExportCommercialDocumentFilename({
              ...values,
              commercialDocumentTypeLabel,
            } as formExportCommercialDocumentValues);
            break;
          case StudentExportTypeEnum.StudentMasterList:
            fileName = `Student Master Records - ${values.school.name}.csv`;
            break;
          case ExportTypeEnum.StudentRenewal:
            fileName = `Regular Student Renewal Records - ${values.school.name}.csv`;
            break;
          case ExportTypeEnum.CcaStudentRenewal:
            fileName = `CCA Student Renewal Records - ${values.school.name}.csv`;
            break;
        }

        fileDownload(parser.parse(csvData), fileName);
      })
      .catch((e: any) => {
        notify(`${e.message}`, "warning");
      })
      .finally(() => {
        // Reset loading state and progress regardless of success or failure
        setCSVLoading(false);
        setIsLoading && setIsLoading(false);
        setProgress({
          current: null,
          total: null,
        });
      });
  };

  const labelButton = useMemo(() => {
    if (!isCSVLoading) {
      return "Export";
    }

    if (progress.current === null && progress.total === null) {
      return `Fetching ${capitalize(values.exportType)}`;
    }

    if (progress.current !== null && progress.total !== null) {
      let label = `Fetching Data ${progress.current}/${progress.total}`;
      switch (values.exportType) {
        case ExportTypeEnum.Parent:
          label = `Fetching MBC Data ${progress.current}/${progress.total}`;
          break;
        case StudentExportTypeEnum.Student:
        case StudentExportTypeEnum.StudentMasterList:
          label = `Fetching Student Data ${progress.current}/${progress.total}`;
          break;
        case AssignmentReportTypeEnum.AreaReport:
          label = `Fetching Area Data ${progress.current}/${progress.total}`;
          break;
        case AssignmentReportTypeEnum.PassengerReport:
          label = `Fetching Passenger Data ${progress.current}/${progress.total}`;
          break;
        case AssignmentReportTypeEnum.AmBusArrivalReport:
          label = `Fetching Assignment Data ${progress.current}/${progress.total}`;
          break;
        case AssignmentReportTypeEnum.AssignmentSummaryReport:
          label = `Fetching Assignment Summary Data ${progress.current}/${progress.total}`;
          break;
        case ExportTypeEnum.CommercialDocument:
          label = `Fetching Commercial Document Data ${progress.current}/${progress.total}`;
          break;
        case ExportTypeEnum.StudentRenewal:
          label = `Fetching Regular Student Renewal Data ${progress.current}/${progress.total}`;
          break;
        case ExportTypeEnum.CcaStudentRenewal:
          label = `Fetching CCA Student Renewal Data ${progress.current}/${progress.total}`;
          break;
      }

      return label;
    }

    return "Downloading CSV";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCSVLoading, progress]);

  useEffect(() => {
    if (!values.exportType) {
      setIsDisabled(true);
      return;
    }

    if (
      values.exportType === StudentExportTypeEnum.Student ||
      values.exportType === StudentExportTypeEnum.StudentMasterList
    ) {
      const isFormInvalid = exportStudentValidation({
        ...values,
        ...studentMasterListValues,
      } as formExportStudentValues);
      setIsDisabled(isFormInvalid);
      return;
    }

    if (values.exportType === ExportTypeEnum.CommercialDocument) {
      const isFormInvalid = exportCommercialDocumentValidation(
        values as formExportCommercialDocumentValues,
      );
      setIsDisabled(isFormInvalid);
      return;
    }

    if (
      (values.exportType === AssignmentReportTypeEnum.AreaReport ||
        values.exportType === AssignmentReportTypeEnum.PassengerReport) &&
      !values.estimatedStartedAt
    ) {
      setIsDisabled(true);
      return;
    }

    if (
      values.exportType === AssignmentReportTypeEnum.PassengerReport &&
      values.isFilterByWave &&
      !values.busSchedulePlanId
    ) {
      setIsDisabled(true);
      return;
    }

    if (
      (values.exportType === AssignmentReportTypeEnum.AmBusArrivalReport ||
        values.exportType ===
          AssignmentReportTypeEnum.AssignmentSummaryReport) &&
      (!values.startDate || !values.endDate)
    ) {
      setIsDisabled(true);
      return;
    }

    if (
      values.exportType === AssignmentReportTypeEnum.AmBusArrivalReport &&
      values.isDownloadAllAmWaves === false &&
      (!values.busSchedulePlanIds || values.busSchedulePlanIds.length === 0)
    ) {
      setIsDisabled(true);
      return;
    }

    setIsDisabled(false);
  }, [values]);

  return (
    <Button
      onClick={exportCSV}
      label={labelButton}
      color="primary"
      disabled={isDisabled}
    />
  );
};

const ExportTypeInput = ({ isDisabled }: any) => {
  const isDownloadAllAmWavesField = useField("isDownloadAllAmWaves");
  const busSchedulePlanIdsField = useField("busSchedulePlanIds");
  const isFilterByWaveField = useField("isFilterByWave");
  const busSchedulePlanIdField = useField("busSchedulePlanId");

  return (
    <SelectInput
      source="exportType"
      label="Export Type"
      choices={exportTypeList}
      disabled={isDisabled}
      onChange={() => {
        isDownloadAllAmWavesField &&
          isDownloadAllAmWavesField.input.onChange(true);
        busSchedulePlanIdsField && busSchedulePlanIdsField.input.onChange([]);
        isFilterByWaveField && isFilterByWaveField.input.onChange(false);
        busSchedulePlanIdField && busSchedulePlanIdField.input.onChange("");
      }}
    />
  );
};

const ExportDataFeatures = () => {
  const navMode = useSelector((state: ReduxState) => state.navMode);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const classes = useStyles();

  const breadcrumb = [
    { link: "/exportDataFeatures", title: "Export Data Features" },
  ];

  return (
    <>
      <HeaderContent
        breadcrumb={breadcrumb}
        headerTitle="Export Data Features"
      />

      <SimpleForm
        initialValues={{ school: navMode.school }}
        toolbar={null}
        className={classes.form}
      >
        <Card>
          <CardContent>
            <SanitizedGrid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">Export Data</Typography>
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body1" gutterBottom>
                    Please select the export type from the dropdown and click
                    the Export button to proceed.
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    To export <strong>Student Master List</strong>, select{" "}
                    <strong>Student Data</strong> on{" "}
                    <strong>Export Type</strong> dropdown option and toggle{" "}
                    <strong>Include Parent</strong>,{" "}
                    <strong>Include Student Schedule</strong>,{" "}
                    <strong>Include Estimated Revenue</strong>, and{" "}
                    <strong>Include Route Plan</strong>
                  </Typography>
                </Alert>
              </Grid>

              <Grid item xs={4}>
                <SanitizedGrid container spacing={2}>
                  <Grid item xs={12}>
                    <ExportTypeInput />
                  </Grid>

                  {/* Export Student Data */}
                  <FormDataConsumer>
                    {({ formData }) =>
                      (formData.exportType === StudentExportTypeEnum.Student ||
                        formData.exportType ===
                          StudentExportTypeEnum.StudentMasterList) && (
                        <Grid
                          item
                          xs={12}
                          className={classes.exportStudentInputContainer}
                        >
                          <ExportStudentInput
                            isLoading={isLoading}
                            schoolId={navMode.school.id}
                            isMasterList={
                              formData.exportType ===
                              StudentExportTypeEnum.StudentMasterList
                            }
                          />
                        </Grid>
                      )
                    }
                  </FormDataConsumer>

                  {/* Export Assignment Data */}
                  <FormDataConsumer>
                    {({ formData }) =>
                      (formData.exportType ===
                        AssignmentReportTypeEnum.AreaReport ||
                        formData.exportType ===
                          AssignmentReportTypeEnum.PassengerReport) && (
                        <Grid
                          item
                          xs={12}
                          className={classes.exportInputContainer}
                        >
                          <CustomDatePickerInput
                            name="estimatedStartedAt"
                            label="Assignment Date"
                          />
                        </Grid>
                      )
                    }
                  </FormDataConsumer>

                  <FormDataConsumer>
                    {({ formData }) =>
                      formData.exportType ===
                        AssignmentReportTypeEnum.PassengerReport && (
                        <Grid
                          item
                          xs={12}
                          className={classes.exportInputContainer}
                        >
                          <BooleanInput
                            source="isFilterByWave"
                            label="Filter Assignment Passenger Report By Wave"
                            defaultValue={false}
                            disabled={isLoading}
                            className={classes.filterByWaveInput}
                          />

                          {formData.isFilterByWave && (
                            <WaveInput
                              source="busSchedulePlanId"
                              label="Wave"
                              schoolId={navMode.school?.id}
                              isDisabled={isLoading}
                            />
                          )}
                        </Grid>
                      )
                    }
                  </FormDataConsumer>

                  <FormDataConsumer>
                    {({ formData }) =>
                      (formData.exportType ===
                        AssignmentReportTypeEnum.AmBusArrivalReport ||
                        formData.exportType ===
                          AssignmentReportTypeEnum.AssignmentSummaryReport) && (
                        <Grid
                          item
                          xs={12}
                          className={classes.exportInputContainer}
                        >
                          <CustomDateRangeInput
                            fromSource="startDate"
                            toSource="endDate"
                            fromLabel="Start Date"
                            toLabel="End Date"
                            isDisabled={isLoading}
                            gridItemWidth={6}
                          />
                        </Grid>
                      )
                    }
                  </FormDataConsumer>

                  <FormDataConsumer>
                    {({ formData }) =>
                      formData.exportType ===
                        AssignmentReportTypeEnum.AmBusArrivalReport && (
                        <Grid
                          item
                          xs={12}
                          className={classes.exportInputContainer}
                        >
                          <BooleanInput
                            source="isDownloadAllAmWaves"
                            label="Download All AM Waves within Date Range"
                            defaultValue={true}
                            disabled={isLoading}
                            className={classes.filterByAllWavesInput}
                          />

                          {!formData.isDownloadAllAmWaves && (
                            <WaveInput
                              source="busSchedulePlanIds"
                              label="Waves"
                              schoolId={navMode.school?.id}
                              scheduleTypeFilter="to_school"
                              isDisabled={isLoading}
                              multipleSelection
                            />
                          )}
                        </Grid>
                      )
                    }
                  </FormDataConsumer>
                  {/* End: Export Assignment Data */}

                  {/* Export Commercial Document */}
                  <FormDataConsumer>
                    {({ formData }) =>
                      formData.exportType ===
                        ExportTypeEnum.CommercialDocument && (
                        <Grid
                          item
                          xs={12}
                          className={classes.exportInputContainer}
                        >
                          <SelectInput
                            source="commercialDocumentType"
                            label="Commercial Document Type"
                            choices={[
                              { id: "Invoice", name: "Invoice" },
                              { id: "CreditNote", name: "Credit Note" },
                            ]}
                            disabled={isLoading}
                          />

                          <ExportCommercialDocumentInput
                            documentType={formData.commercialDocumentType}
                            isDisabled={isLoading}
                          />
                        </Grid>
                      )
                    }
                  </FormDataConsumer>

                  {/* Export Regular Student Renewal */}
                  <FormDataConsumer>
                    {({ formData }) =>
                      formData.exportType === ExportTypeEnum.StudentRenewal && (
                        <Grid
                          item
                          xs={12}
                          className={classes.exportInputContainer}
                        >
                          <ReferenceInput
                            label="Academic Year"
                            source="academicYearId"
                            reference="academicYears"
                            filter={{
                              SchoolId: Number(navMode.school?.id),
                            }}
                            alwaysOn
                          >
                            <SelectInput optionText="name" />
                          </ReferenceInput>
                          <SelectInput
                            label="Status"
                            source="status"
                            choices={notRenewedStudentStatusList}
                            alwaysOn
                          />
                        </Grid>
                      )
                    }
                  </FormDataConsumer>

                  {/* Export CCA Student Renewal */}
                  <FormDataConsumer>
                    {({ formData }) =>
                      formData.exportType ===
                        ExportTypeEnum.CcaStudentRenewal && (
                        <Grid
                          item
                          xs={12}
                          className={classes.exportInputContainer}
                        >
                          <ReferenceInput
                            label="CCA Academic Year"
                            source="ccaAcademicYearId"
                            reference="ccaAcademicYears"
                            filter={{
                              SchoolId: Number(navMode.school?.id),
                            }}
                            alwaysOn
                          >
                            <SelectInput optionText="name" />
                          </ReferenceInput>
                          <SelectInput
                            label="Status"
                            source="status"
                            choices={notRenewedCcaStudentStatusList}
                            alwaysOn
                          />
                        </Grid>
                      )
                    }
                  </FormDataConsumer>

                  <Grid item xs={12}>
                    <ExportButton setIsLoading={setIsLoading} />
                  </Grid>
                </SanitizedGrid>
              </Grid>

              <Grid
                item
                xs={8}
                className={classes.exportTypeInformationContainer}
              >
                <ExportTypeInformationComponent />
              </Grid>
            </SanitizedGrid>
          </CardContent>
        </Card>
      </SimpleForm>
    </>
  );
};

export default withRouter(ExportDataFeatures);
